// DisbursedLoanPage.jsx (PROD) — with bulk archive / multi-select support
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FiDownload, FiUpload } from 'react-icons/fi';

function formatDateTime(datetime) {
  if (
    !datetime ||
    datetime === '1970-01-01 00:00:00' ||
    datetime === '0000-00-00 00:00:00'
  )
    return '-';
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function formatTime(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const [hours, minutes] = timeStr.split(':');
  if (!hours || !minutes) return '';
  const date = new Date();
  date.setHours(parseInt(hours));
  date.setMinutes(parseInt(minutes));
  date.setSeconds(0);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function formatDateOnly(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

function DisbursedLoanPage({ currentUser }) {
  const [loans, setLoans] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchApprovalFrom, setSearchApprovalFrom] = useState('');
  const [searchApprovalTo, setSearchApprovalTo] = useState('');
  const [searchRegion, setSearchRegion] = useState('');
  const [searchStatus, setSearchStatus] = useState('');

  // NEW: selected loans for bulk archive
  const [selectedLoans, setSelectedLoans] = useState([]);

  const fetchLoans = async () => {
    try {
      const response = await fetch(
        'https://api.stitchtechsolutions.com/api/list_loans.php',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: currentUser?.role })
        }
      );
      const data = await response.json();
      if (data.status === 'success') {
        const disbursed = data.loans.filter(
          (loan) => loan.loan_status === 'Disbursed' && loan.is_archived !== '1'
        );
        setLoans(disbursed);
        // reset multi-select whenever list refreshes
        setSelectedLoans([]);
      } else {
        Swal.fire('Error', data.message, 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to load loan data', 'error');
    }
  };

  useEffect(() => {
    fetchLoans();
  }, [currentUser]);

  const onEditChange = (field, value) =>
    setEditingData((prev) => ({ ...prev, [field]: value }));

  const onStartEdit = (loanId) => {
    const loan = loans.find((l) => l.id === loanId);
    if (loan) {
      setEditingId(loanId);
      setEditingData({ ...loan });
    }
  };

  const onSaveEdit = async () => {
    const response = await fetch(
      'https://api.stitchtechsolutions.com/api/add_or_update_loan.php',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingData)
      }
    );
    const result = await response.json();
    Swal.fire(result.status === 'success' ? 'Success' : 'Error', result.message, result.status);
    if (result.status === 'success') {
      await fetchLoans();
      setEditingId(null);
    }
  };

  const onCancelEdit = () => setEditingId(null);

  const onToggleVisibility = async (loanId, newVisibility) => {
    try {
      const response = await fetch(
        'https://api.stitchtechsolutions.com/api/toggle_loan_visibility.php',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ loan_id: loanId, visibility: newVisibility })
        }
      );
      const data = await response.json();
      if (data.status === 'success') {
        await fetchLoans();
        Swal.fire('Updated', 'Visibility updated successfully', 'success');
      } else {
        Swal.fire('Error', data.message, 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Visibility update failed', 'error');
    }
  };

  const onArchiveLoan = async (loanId) => {
    const confirm = await Swal.fire({
      title: 'Archive this loan?',
      text: 'It will move to Archived Loans and no longer appear here.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, archive it',
      cancelButtonText: 'Cancel'
    });

    if (confirm.isConfirmed) {
      try {
        const response = await fetch(
          'https://api.stitchtechsolutions.com/api/archive_loan.php',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ loan_id: loanId })
          }
        );
        const result = await response.json();
        Swal.fire(
          result.status === 'success' ? 'Archived!' : 'Error',
          result.message,
          result.status
        );
        if (result.status === 'success') {
          await fetchLoans();
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to archive the loan', 'error');
      }
    }
  };

  // NEW: Bulk archive selected loans
  const onArchiveSelectedLoans = async () => {
    if (selectedLoans.length === 0) return;

    const loansToArchive = loans.filter((loan) => selectedLoans.includes(loan.id));

    const htmlList = loansToArchive
      .map(
        (loan) =>
          `<li><strong>${loan.client_name || 'N/A'}</strong> - ${
            loan.type_of_loan || ''
          } (ID: ${loan.id})</li>`
      )
      .join('');

    const confirm = await Swal.fire({
      title: `Archive ${selectedLoans.length} loan(s)?`,
      html: `
        <p>They will move to Archived Loans and no longer appear here.</p>
        <div style="max-height: 250px; overflow-y: auto; text-align: left;">
          <ul style="padding-left: 1.2rem; margin: 0;">
            ${htmlList}
          </ul>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, archive all',
      cancelButtonText: 'Cancel',
      width: '600px'
    });

    if (!confirm.isConfirmed) return;

    let successCount = 0;
    let failCount = 0;

    for (const loanId of selectedLoans) {
      try {
        const response = await fetch(
          'https://api.stitchtechsolutions.com/api/archive_loan.php',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ loan_id: loanId })
          }
        );
        const result = await response.json();
        if (result.status === 'success') {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }

    await Swal.fire(
      'Done',
      `Successfully archived ${successCount} loan(s).${
        failCount ? `<br/>Failed: ${failCount}` : ''
      }`,
      failCount ? 'warning' : 'success'
    );

    setSelectedLoans([]);
    await fetchLoans();
  };

  // Filters
  const filteredLoans = loans.filter((loan) => {
    const matchesSearchTerm = Object.values(loan).some((val) =>
      val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesApprovalFrom = searchApprovalFrom
      ? loan.approval_date >= searchApprovalFrom
      : true;
    const matchesApprovalTo = searchApprovalTo ? loan.approval_date <= searchApprovalTo : true;
    const matchesRegion = searchRegion ? loan.region === searchRegion : true;
    const matchesStatus = searchStatus ? loan.loan_status === searchStatus : true;
    return (
      matchesSearchTerm &&
      matchesApprovalFrom &&
      matchesApprovalTo &&
      matchesRegion &&
      matchesStatus
    );
  });

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isStandard = currentUser?.role === 'standard';
  const isSuperAdmin = currentUser?.role === 'superadmin';

  // helpers for selection
  const toggleSelectLoan = (loanId) => {
    setSelectedLoans((prev) =>
      prev.includes(loanId) ? prev.filter((id) => id !== loanId) : [...prev, loanId]
    );
  };

  const isAllSelected =
    filteredLoans.length > 0 &&
    filteredLoans.every((loan) => selectedLoans.includes(loan.id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const filteredIds = filteredLoans.map((loan) => loan.id);
      setSelectedLoans((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      const filteredIds = filteredLoans.map((loan) => loan.id);
      setSelectedLoans((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  // ---- CE code helpers ----
  const renderCeOptions = () =>
    Array.from({ length: 21 }, (_, i) => {
      const code = `CE${i.toString().padStart(2, '0')}`;
      return (
        <option key={code} value={code}>
          {code}
        </option>
      );
    });

  const NoEntry = () => (
    <span style={{ opacity: 0.6, fontStyle: 'italic' }}>No entry yet</span>
  );

  return (
    <div>
      <h2>Disbursed Loans</h2>

      {/* Filters */}
      <div
        style={{
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Search:</label>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '0.5rem', width: '200px' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>
            Approval Date From:
          </label>
          <input
            type="date"
            value={searchApprovalFrom}
            onChange={(e) => setSearchApprovalFrom(e.target.value)}
            style={{ padding: '0.4rem' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>
            Approval Date To:
          </label>
          <input
            type="date"
            value={searchApprovalTo}
            onChange={(e) => setSearchApprovalTo(e.target.value)}
            style={{ padding: '0.4rem' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Region:</label>
          <select
            value={searchRegion}
            onChange={(e) => setSearchRegion(e.target.value)}
            style={{ padding: '0.4rem' }}
          >
            <option value="">All Regions</option>
            <option>NCR</option>
            <option>CAR</option>
            <option>Region I</option>
            <option>Region II</option>
            <option>Region III</option>
            <option>Region IV-A</option>
            <option>Region IV-B</option>
            <option>Region V</option>
            <option>Region VI</option>
            <option>Region VII</option>
            <option>Region VIII</option>
            <option>Region IX</option>
            <option>Region X</option>
            <option>Region XI</option>
            <option>Region XII</option>
            <option>Region XIII</option>
            <option>BARMM</option>
          </select>
        </div>
      </div>

      {/* Bulk archive controls (superadmin only) */}
      {isSuperAdmin && (
        <div
          style={{
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}
        >
          <button
            onClick={onArchiveSelectedLoans}
            disabled={selectedLoans.length === 0}
            className="btn btn-sm btn-danger"
          >
            Archive Selected ({selectedLoans.length})
          </button>

          {filteredLoans.length > 0 && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleSelectAll}
              />
              Select all on this page
            </label>
          )}
        </div>
      )}

      <table className="user-table">
        <thead>
          <tr>
            <th>Type of Loan</th>
            <th>Approval Date</th>
            <th>Approval Time</th>
            <th>Region</th>
            {isAdmin && <th>Employee ID</th>}
            {isAdmin && <th>Client Name</th>}
            {isAdmin && <th>Initial</th>}
            {isStandard && <th>Client</th>}
            {isAdmin && <th>Verifier</th>}
            <th>Loan Status</th>
            <th>Remarks</th>
            <th>Date and Time Disbursed</th>
            {isAdmin && <th>CE Code LMS</th>}
            <th>CE Code NOAH</th>
            {isAdmin && <th>Time and Date</th>}
            {isSuperAdmin && <th>Visible</th>}
            {isSuperAdmin && <th>Action</th>}
            {isSuperAdmin && <th>Archive / Select</th>}
          </tr>
        </thead>
        <tbody>
          {filteredLoans.map((loan) => {
            const isEditing = loan.id === editingId;
            const isSelected = selectedLoans.includes(loan.id);

            return (
              <tr key={loan.id}>
                {isEditing ? (
                  <td>
                    <input
                      value={editingData.type_of_loan || ''}
                      onChange={(e) => onEditChange('type_of_loan', e.target.value)}
                    />
                  </td>
                ) : (
                  <td>{loan.type_of_loan}</td>
                )}

                <td>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editingData.approval_date || ''}
                      onChange={(e) => onEditChange('approval_date', e.target.value)}
                    />
                  ) : (
                    formatDateOnly(loan.approval_date)
                  )}
                </td>

                <td>
                  {isEditing ? (
                    <input
                      type="time"
                      value={editingData.approval_time || ''}
                      onChange={(e) => onEditChange('approval_time', e.target.value)}
                    />
                  ) : (
                    formatTime(loan.approval_time)
                  )}
                </td>

                <td>
                  {isEditing ? (
                    <input
                      value={editingData.region || ''}
                      onChange={(e) => onEditChange('region', e.target.value)}
                    />
                  ) : (
                    loan.region
                  )}
                </td>

                {isAdmin && <td>{loan.employee_id}</td>}

                {isAdmin &&
                  (isEditing ? (
                    <td>
                      <input
                        value={editingData.client_name || ''}
                        onChange={(e) => onEditChange('client_name', e.target.value)}
                      />
                    </td>
                  ) : (
                    <td>{loan.client_name}</td>
                  ))}

                <td>
                  {isEditing ? (
                    <input
                      value={editingData.initial || ''}
                      onChange={(e) => onEditChange('initial', e.target.value)}
                    />
                  ) : (
                    loan.initial
                  )}
                </td>

                {isAdmin && (
                  <td>
                    {isEditing ? (
                      <input
                        value={editingData.verifier || ''}
                        onChange={(e) => onEditChange('verifier', e.target.value)}
                      />
                    ) : (
                      loan.verifier
                    )}
                  </td>
                )}

                <td>
                  {isEditing && isAdmin ? (
                    <select
                      value={editingData.loan_status || ''}
                      onChange={(e) => onEditChange('loan_status', e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Reprocessing">Reprocessing</option>
                      <option value="For Final Checking">For Final Checking</option>
                      <option value="Not Yet Started">Not Yet Started</option>
                      <option value="Hold">Hold</option>
                      <option value="Disapproved">Disapproved</option>
                      <option value="Disbursed">Disbursed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Archive">Archive</option>
                    </select>
                  ) : (
                    <span
                      className={`status-badge ${loan.loan_status
                        ?.toLowerCase()
                        .replace(/\s/g, '-')}`}
                    >
                      {loan.loan_status}
                    </span>
                  )}
                </td>

                <td>
                  {isEditing ? (
                    <input
                      value={editingData.remarks || ''}
                      onChange={(e) => onEditChange('remarks', e.target.value)}
                    />
                  ) : (
                    loan.remarks
                  )}
                </td>

                <td>{formatDateTime(loan.disbursed_date)}</td>

                {/* CE Code LMS (admin-only) with fallback */}
                {isAdmin && (
                  <td>
                    {isEditing ? (
                      <select
                        value={editingData.ce_code || ''}
                        onChange={(e) => onEditChange('ce_code', e.target.value)}
                      >
                        <option value="">--Select--</option>
                        {renderCeOptions()}
                      </select>
                    ) : loan.ce_code ? (
                      loan.ce_code
                    ) : (
                      <NoEntry />
                    )}
                  </td>
                )}

                {/* CE Code NOAH (visible to everyone) with fallback */}
                <td>
                  {isEditing && isAdmin ? (
                    <select
                      value={editingData.ce_code_noah || ''}
                      onChange={(e) => onEditChange('ce_code_noah', e.target.value)}
                    >
                      <option value="">--Select--</option>
                      {renderCeOptions()}
                    </select>
                  ) : loan.ce_code_noah ? (
                    loan.ce_code_noah
                  ) : (
                    <NoEntry />
                  )}
                </td>

                {isAdmin && <td>{formatDateTime(loan.time_and_date)}</td>}

                {isSuperAdmin && (
                  <td>
                    {isEditing ? (
                      <select
                        value={editingData.show_to_standard || 'yes'}
                        onChange={(e) =>
                          onEditChange('show_to_standard', e.target.value)
                        }
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (
                      <button
                        onClick={() => {
                          const newVisibility =
                            loan.show_to_standard === 'yes' ? 'no' : 'yes';
                          Swal.fire({
                            title: 'Are you sure?',
                            text: `This will ${
                              newVisibility === 'yes' ? 'show to' : 'hide from'
                            } standard users.`,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Yes, proceed',
                            cancelButtonText: 'Cancel'
                          }).then((result) => {
                            if (result.isConfirmed) {
                              onToggleVisibility(loan.id, newVisibility);
                            }
                          });
                        }}
                        className={`visibility-toggle ${
                          loan.show_to_standard === 'yes' ? 'visible' : 'hidden'
                        }`}
                      >
                        {loan.show_to_standard === 'yes'
                          ? 'Hide from Standard'
                          : 'Show to Standard'}
                      </button>
                    )}
                  </td>
                )}

                {isSuperAdmin && (
                  <td>
                    {isEditing ? (
                      <>
                        <button onClick={onSaveEdit}>Save</button>
                        <button onClick={onCancelEdit}>Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => onStartEdit(loan.id)}>Edit</button>
                    )}
                  </td>
                )}

                {isSuperAdmin && (
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        justifyContent: 'flex-start'
                      }}
                    >
                      <button
                        onClick={() => onArchiveLoan(loan.id)}
                        className="btn btn-sm btn-danger"
                        title="Archive this loan"
                      >
                        Archive
                      </button>

                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.8rem',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectLoan(loan.id)}
                        />
                        <span>Select</span>
                      </label>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DisbursedLoanPage;
